<template>
  <div>
    <div class="row">
      <div class="col-md-8 mb-3">
        <div class="position-relative">
          <le-loading v-if="isCalendarLoading"></le-loading>
          <div class="calendar-header d-flex flex-column flex-md-row justify-content-md-between align-items-center align-items-md-end mb-1 px-3 px-md-0">
            <div class="d-flex flex-column align-items-center align-items-md-start bg-white rounded p-2">
              <div class="mb-2 d-flex align-items-center">
                <date-picker v-model="selectedDate" @input="onDateSelected" :popover="{ visibility: 'click' }" :locale="language">
                  <template v-slot="{ togglePopover }">
                    <div class="d-flex align-items-center">
                      <button type="button" class="btn btn-sm btn-info mr-2" @click.stop="goToPrev">
                        <i class="fa fa-arrow-left"></i>
                      </button>
                      <button type="button" class="btn btn-sm btn-light font-w600" @click="togglePopover">
                        <i class="fa fa-calendar mr-2"></i> {{ currentCalendarTitle }}
                      </button>
                      <button type="button" class="btn btn-sm btn-info ml-2" @click.stop="goToNext">
                        <i class="fa fa-arrow-right"></i>
                      </button>
                    </div>
                  </template>
                </date-picker>
              </div>
              <div>
                <button class="btn btn-sm mr-2" :class="{'btn-success': currentCalendarType === 'timeGridWeek'}" @click="changeView('timeGridWeek')">Week</button>
                <button class="btn btn-sm" :class="{'btn-success': currentCalendarType === 'timeGridDay'}" @click="changeView('timeGridDay')">Day</button>
              </div>
            </div>
            <div>
              <div class="mb-0 bg-white rounded">
                <div class="px-2 pt-2 text-center font-w600">{{ $tc('venue.name', 2) }}</div>
                <div class="p-2">
                  <multiselect
                    style="min-width: 250px"
                    :multiple="false"
                    :placeholder="$tc('venue.name', 1)"
                    :custom-label="(o) => o.name"
                    @select="refreshCalendar"
                    v-model="selectedVenue"
                    :allow-empty="false"
                    :options="sortedVenues">
                    <template #option="props">
                      <div class="d-flex align-items-center">
                        <span
                          v-if="props.option.uuid"
                          class="venue-usage-dot mr-1"
                          :class="scheduleVenues.includes(props.option.uuid) ? 'venue-usage-dot--used' : 'venue-usage-dot--unused'"
                          v-tooltip="scheduleVenues.includes(props.option.uuid) ? $t('venue.used') : $t('venue.unused')">
                        </span>
                        <span>{{ props.option.name }}</span>
                      </div>
                    </template>
                  </multiselect>
                </div>
              </div>
            </div>
          </div>
          <FullCalendar ref="fullCalendar" :options="calendarOptions" />
        </div>
      </div>

      <div class="col-md-4">
        <div class="games-to-schedule bg-white align-items-baseline">
          <div class="w-100">
            <div class="games-to-schedule-leagues w-100 p-2 m-0">
              <multiselect :multiple="true"
                           track-by="uuid"
                           :custom-label="(option) => option.name"
                           placeholder="Select team"
                           v-model="teamFilter"
                           :options="teamOptions">
                <template #option="props">
                  <div class="d-flex justify-content-between">
                    <div>
                      <span :class="props.option.scheduledGamesCount > 0 ? 'badge-success' : 'badge-warning'" class="badge mr-2">{{ props.option.scheduledGamesCount }}</span>
                      <span class="option__title">{{ props.option.name }}</span>
                    </div>
                    <span :class="getTeamNonPlayedGamesCountClass(props.option.uuid)" class="badge mr-2">{{ props.option.nonPlayedGamesCount }}</span>
                  </div>
                </template>
              </multiselect>
            </div>
          </div>
          <div>
            <div class="games-to-schedule-sort text-right p-2">
              <div class="btn-group">
                <button class="btn btn-sm" :class="{'btn-success': orderByNonPlayedGames}" @click="setOrderByNonPlayedGames(true)"><i v-if="orderByNonPlayedGames" class="fa fa-sort-amount-desc"></i> {{ $tc('game.status.NOT_PLAYED', 2) }}</button>
                <button class="btn btn-sm" :class="{'btn-success': !orderByNonPlayedGames}" @click="setOrderByNonPlayedGames(false)"><i v-if="!orderByNonPlayedGames" class="fa fa-sort-amount-asc"></i> {{ $tc('round', 1) }}</button>
              </div>
            </div>
          </div>
        </div>

        <div class="position-relative p-3 overflow-y-auto games-to-schedule" style="height: 873px;">
          <le-loading v-if="isGamesToScheduleLoading"></le-loading>
          <ul id="js-events-schedule-view" class="list list-events">
            <li
              v-for="(event, index) in sortedGamesToSchedule"
              :key="`events-${index}`"
              :data-id="event.extendedProps.uuid"
              @click="openModalFromEvent(event)"
              class="mb-3 shadow"
            >
              <div
                class="js-event rounded"
                :style="{'background-color': event.extendedProps.backgroundColor, 'color': event.extendedProps.textColor}"
              >
                <div class="d-flex align-items-stretch">
                  <div class="d-flex align-items-center rounded-left justify-content-center bg-white" style="width: 24px; flex: none;">
                    <drag-icon style="width: 10px; color: #808080" />
                  </div>
                  <div class="w-100">
                    <div class="d-flex flex-column js-event-teams">
                      <div class="p-1 bg-white text-black-50 js-event-home-team d-flex justify-content-between align-items-center rounded-0">
                        <span><span :class="getTeamGamesCount(event.extendedProps.teamA?.uuid).scheduledGamesCount > 0 ? 'badge-success' : 'badge-warning'" class="badge mr-2">{{ getTeamGamesCount(event.extendedProps.teamA?.uuid).scheduledGamesCount }}</span>{{ event.extendedProps.teamA?.name ?? 'TBD' }}</span>
                        <span :class="getTeamNonPlayedGamesCountClass(event.extendedProps.teamA?.uuid)" class="badge">{{ getTeamGamesCount(event.extendedProps.teamA?.uuid).nonPlayedGamesCount }}</span>
                      </div>
                      <div class="p-1 bg-white text-black-50 js-event-away-team d-flex justify-content-between align-items-center">
                        <span><span :class="getTeamGamesCount(event.extendedProps.teamB?.uuid).scheduledGamesCount > 0 ? 'badge-success' : 'badge-warning'" class="badge mr-2">{{ getTeamGamesCount(event.extendedProps.teamB?.uuid).scheduledGamesCount }}</span>{{ event.extendedProps.teamB?.name ?? 'TBD' }}</span>
                        <span :class="getTeamNonPlayedGamesCountClass(event.extendedProps.teamB?.uuid)" class="badge">{{ getTeamGamesCount(event.extendedProps.teamB?.uuid).nonPlayedGamesCount }}</span>
                      </div>
                    </div>
                    <div class="px-2 py-1">{{ getRoundName(event.extendedProps.round, event.extendedProps.playoffRound) }}</div>
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <modal name="schedule-view-calendar-game" :adaptive="true" height="auto">
      <form @submit.prevent="scheduleGameFromForm">
        <ntm-block :collapsable="false" :title="selectedGame.title">
          <template #options>
            <button type="button" class="btn btn-sm btn-light" @click="$modal.hide('schedule-view-calendar-game')" v-tooltip="$t('close')">
              <i class="fa fa-times"></i>
            </button>
          </template>
          <formly-form :form="scheduleGameForm" :model="selectedGame" :fields="scheduleGameFields"></formly-form>
          <div class="d-flex mb-2">
            <button type="submit" class="btn btn-success mr-2" :disabled="isSaving || isRemovingFromCalendar">
              <i v-if="isSaving" class="fa fa-cog fa-spin mr-2"></i>
              <span>{{ $t('action.save') }}</span>
            </button>
            <button v-if="selectedGame.status === 'SCHEDULED'" type="button" @click="cancelGameSchedule" class="btn btn-danger" :disabled="isSaving || isRemovingFromCalendar">
              <i v-if="isRemovingFromCalendar" class="fa fa-cog fa-spin mr-2"></i>
              <span>{{ $t('action.removeFromCalendar') }}</span>
            </button>
          </div>
        </ntm-block>
      </form>
    </modal>
  </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin, { Draggable } from '@fullcalendar/interaction'
import DashboardService from '@/services/dashboard.service'
import Multiselect from 'vue-multiselect'
import LeLoading from '@/components/ntm/LeLoading.vue'
import GameService from '@/services/game.service'
import scheduleGameFields from '@/views/schedules/games/formDefinitions/scheduleFormDefinition'
import { useTimeline } from '@/use/useTimeline'
import { mapState } from 'vuex'
import Vue from 'vue'
import { DatePicker } from 'v-calendar'
import LeGameCalendarEvent from '@/components/le/LeGameCalendarEvent.vue'
import DragIcon from '@/components/svg/DragIcon.vue'

export default {
  components: { DragIcon, LeLoading, Multiselect, FullCalendar, DatePicker },
  props: {
    scheduleUuid: {
      type: String,
      required: true
    }
  },
  data () {
    const lastView = localStorage.getItem('sc_fc_last_view') || 'timeGridWeek'
    const storedDate = localStorage.getItem('sc_fc_last_date')
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const lastDate = (storedDate && new Date(storedDate) >= today) ? storedDate : new Date().toISOString()

    return {
      currentCalendarTitle: null,
      currentCalendarType: null,
      orderByNonPlayedGames: localStorage.getItem('order-by-non-played-games') === 'true',
      gamesToScheduleTeams: [],
      teamFilter: [],
      scheduleGameFields,
      isSaving: false,
      isRemovingFromCalendar: false,
      scheduleGameForm: {},
      selectedGame: {
        title: '',
        status: null,
        schedule: null,
        uuid: '',
        time: '',
        venue: null,
        info: ''
      },
      isCalendarLoading: true,
      isGamesToScheduleLoading: false,
      selectedVenue: null,
      venues: [],
      calendarOptions: {
        firstDay: 1,
        plugins: [timeGridPlugin, interactionPlugin, dayGridPlugin],
        headerToolbar: false,
        locale: 'en',
        initialView: lastView,
        initialDate: lastDate,
        editable: true,
        selectable: true,
        slotMinTime: '06:00:00',
        slotMaxTime: '24:00:00',
        slotDuration: '00:30:00',
        snapDuration: '00:05:00',
        slotLabelInterval: '00:30:00',
        height: 'auto',
        slotLabelFormat: {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        },
        eventTimeFormat: {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        },
        allDaySlot: false,
        events: this.fetchEvents,
        eventClick: this.handleEventClick,
        eventDrop: this.handleEventDrop,
        datesSet: this.handleViewChange,
        eventReceive: this.handleExternalDrop,
        eventContent: this.renderEvent
      },
      gamesToSchedule: [],
      scheduleVenues: [],
      selectedDate: new Date()
    }
  },
  setup () {
    const { fetchCurrentTimeline } = useTimeline()
    return { fetchCurrentTimeline }
  },
  computed: {
    ...mapState(['language']),
    settings () {
      return this.$store.state.settings
    },
    sortedGamesToSchedule () {
      const teamsMap = new Map(this.gamesToScheduleTeams.map(team => [team.uuid, team.nonPlayedGamesCount]))
      return [...this.gamesToSchedule].filter(game => {
        if (this.teamFilter.length > 0) {
          return this.teamFilter.some(team => game.extendedProps.teamA?.uuid === team.uuid || game.extendedProps.teamB?.uuid === team.uuid)
        }
        return true
      }).sort((a, b) => {
        if (a.extendedProps.playoffRound && !b.extendedProps.playoffRound) return -1
        if (!a.extendedProps.playoffRound && b.extendedProps.playoffRound) return 1
        if (a.extendedProps.playoffRound && b.extendedProps.playoffRound) {
          if (a.extendedProps.playoffRound !== b.extendedProps.playoffRound) {
            return b.extendedProps.playoffRound - a.extendedProps.playoffRound
          }
        }
        if (this.orderByNonPlayedGames) {
          const aTotal = (teamsMap.get(a.extendedProps.teamA?.uuid) || 0) + (teamsMap.get(a.extendedProps.teamB?.uuid) || 0)
          const bTotal = (teamsMap.get(b.extendedProps.teamA?.uuid) || 0) + (teamsMap.get(b.extendedProps.teamB?.uuid) || 0)
          if (bTotal !== aTotal) return bTotal - aTotal
        }
        return a.extendedProps.round - b.extendedProps.round
      })
    },
    teamNonPlayedGamesMin () {
      return Math.min(...this.gamesToScheduleTeams.map(t => t.nonPlayedGamesCount))
    },
    teamNonPlayedGamesAverage () {
      return Math.round(this.gamesToScheduleTeams.reduce((acc, t) => acc + t.nonPlayedGamesCount, 0) / this.gamesToScheduleTeams.length)
    },
    teamOptions () {
      return [...this.gamesToScheduleTeams].sort((a, b) => {
        if (b.nonPlayedGamesCount - a.nonPlayedGamesCount !== 0) return b.nonPlayedGamesCount - a.nonPlayedGamesCount
        return a.scheduledGamesCount - b.scheduledGamesCount
      })
    },
    sortedVenues () {
      const selected = this.venues.filter(venue => this.scheduleVenues.includes(venue.uuid))
      const rest = this.venues.filter(venue => !this.scheduleVenues.includes(venue.uuid))
      return [this.allVenuesOption, ...selected, ...rest]
    },
    allVenuesOption () {
      return { uuid: null, name: this.$t('venue.all') }
    }
  },
  watch: {
    language (newLang) {
      this.$refs.fullCalendar.getApi().setOption('locale', newLang)
    },
    selectedVenue (value) {
      if (value) localStorage.setItem('selected-venue', value.uuid || 'all')
    }
  },
  created () {
    const hour12 = this.settings.formatOfTime === '12h-clock'
    this.calendarOptions.locale = this.language
    this.calendarOptions.slotLabelFormat = { hour: '2-digit', minute: '2-digit', hour12 }
    this.calendarOptions.eventTimeFormat = { hour: '2-digit', minute: '2-digit', hour12 }
  },
  async mounted () {
    this.$nextTick(() => {
      if (this.$refs.fullCalendar) {
        this.$refs.fullCalendar.getApi().updateSize()
      }
    })

    const res = await DashboardService.getCalendarVenues()
    this.venues = res.data

    const storedVenueUuid = localStorage.getItem('selected-venue')
    if (!storedVenueUuid || storedVenueUuid === 'all') {
      this.selectedVenue = this.allVenuesOption
    } else {
      this.selectedVenue = this.venues.find(v => v.uuid === storedVenueUuid) || this.allVenuesOption
    }

    const container = document.getElementById('js-events-schedule-view')
    if (container) {
      // eslint-disable-next-line no-new
      new Draggable(container, {
        itemSelector: '.js-event',
        eventData: (eventEl) => {
          const gameUuid = eventEl.parentElement.getAttribute('data-id')
          const eventInfo = this.gamesToSchedule.find(e => e.extendedProps.uuid === gameUuid)
          return {
            title: eventInfo.title,
            backgroundColor: eventInfo.extendedProps.backgroundColor,
            textColor: eventInfo.extendedProps.textColor,
            duration: eventInfo.duration,
            durationEditable: eventInfo.durationEditable,
            extendedProps: eventInfo.extendedProps
          }
        }
      })
    }
  },
  methods: {
    goToPrev () {
      this.$refs.fullCalendar.getApi().prev()
    },
    goToNext () {
      this.$refs.fullCalendar.getApi().next()
    },
    changeView (viewName) {
      this.$refs.fullCalendar.getApi().changeView(viewName)
    },
    onDateSelected (date) {
      if (date) this.$refs.fullCalendar.getApi().gotoDate(date)
    },
    refreshCalendar () {
      const api = this.$refs.fullCalendar.getApi()
      api.getEvents().forEach(e => e.remove())
      api.refetchEvents()
    },
    renderEvent (arg) {
      if (arg.event.display === 'background') return
      const EventConstructor = Vue.extend(LeGameCalendarEvent)
      const instance = new EventConstructor({
        propsData: { info: arg, getRoundName: this.getRoundName }
      }).$mount()
      return { domNodes: [instance.$el] }
    },
    getRoundName (round, playoffRound = null) {
      if (!playoffRound) return `${this.$tc('round', 1)} ${round}`
      if (playoffRound > 8) return `${this.$t('playoff.round.bestOf', { round: playoffRound })}, ${this.$tc('game.name', 1)} ${round}`
      return `${this.$t(`playoff.round.${playoffRound}`)}, ${this.$tc('game.name', 1)} ${round}`
    },
    fetchGamesToSchedule () {
      this.isGamesToScheduleLoading = true
      DashboardService.getGamesToSchedule(this.scheduleUuid, this.getCalendarDateRange()).then(response => {
        this.gamesToSchedule = response.data.games.map(game => this.mapGameToEvent(game))
        this.gamesToScheduleTeams = response.data.teams
        this.scheduleVenues = response.data.scheduleVenues || []
      }).finally(() => {
        this.isGamesToScheduleLoading = false
      })
    },
    getCalendarDateRange () {
      const api = this.$refs.fullCalendar.getApi()
      return {
        fromDate: api.view.currentStart,
        toDate: api.view.currentEnd
      }
    },
    openModalFromEvent (event) {
      if (event.extendedProps.status !== 'NOT_PLAYED' && event.extendedProps.status !== 'SCHEDULED') return
      this.selectedGame.title = event.extendedProps.title
      this.selectedGame.schedule = event.extendedProps.schedule
      this.selectedGame.uuid = event.extendedProps.uuid
      this.selectedGame.time = event.extendedProps.time
      this.selectedGame.venue = event.extendedProps.venue
      this.selectedGame.info = event.extendedProps.info
      this.selectedGame.status = event.extendedProps.status
      this.$modal.show('schedule-view-calendar-game')
    },
    handleEventClick (info) {
      this.openModalFromEvent(info.event)
    },
    handleExternalDrop (info) {
      const gameUuid = info.event.extendedProps.uuid

      if (!this.selectedVenue?.uuid) {
        // "All venues" is selected, so we don't know which venue to schedule the game at.
        // Remove the event FullCalendar auto-added and open the modal so the user can pick a venue.
        this.openScheduleModalFromDrop(info.event)
        info.event.remove()
        return
      }

      this.scheduleGameFromEvent(info.event, true)
      this.gamesToSchedule = this.gamesToSchedule.filter(e => e.extendedProps.uuid !== gameUuid)
    },
    openScheduleModalFromDrop (event) {
      this.selectedGame.title = event.extendedProps.title
      this.selectedGame.schedule = event.extendedProps.schedule
      this.selectedGame.uuid = event.extendedProps.uuid
      this.selectedGame.time = this.formatDate(event.start)
      this.selectedGame.venue = null
      this.selectedGame.info = event.extendedProps.info
      this.selectedGame.status = event.extendedProps.status
      this.$modal.show('schedule-view-calendar-game')
    },
    handleEventDrop (info) {
      this.scheduleGameFromEvent(info.event, false, false)
    },
    scheduleGameFromEvent (event, takeVenueFromSelect = true, fetchGamesToSchedule = true) {
      if (fetchGamesToSchedule) this.isGamesToScheduleLoading = true
      GameService.scheduleGame(event.extendedProps.schedule.uuid, event.extendedProps.uuid, {
        time: this.formatDate(event.start),
        venue: takeVenueFromSelect && this.selectedVenue?.uuid ? this.selectedVenue : event.extendedProps.venue
      }).then((res) => {
        event.setExtendedProp('status', res.data.status)
        event.setExtendedProp('venue', res.data.venue)
        event.setExtendedProp('time', res.data.time)
        event.setProp('classNames', this.getColorByStatus(res.data.status))
        if (fetchGamesToSchedule) this.fetchGamesToSchedule()
        this.fetchCurrentTimeline()
      }).catch((err) => {
        console.error('Error scheduling game:', err)
      })
    },
    scheduleGameFromForm () {
      this.isGamesToScheduleLoading = true
      this.isSaving = true
      GameService.scheduleGame(this.selectedGame.schedule.uuid, this.selectedGame.uuid, {
        time: this.selectedGame.time,
        venue: this.selectedGame.venue,
        info: this.selectedGame.info
      }).then(() => {
        this.$modal.hide('schedule-view-calendar-game')
        this.refreshCalendar()
        this.fetchGamesToSchedule()
        this.fetchCurrentTimeline()
      }).catch((err) => {
        this.scheduleGameForm.$errors = err.response.data.errors
      }).finally(() => {
        this.isSaving = false
      })
    },
    cancelGameSchedule () {
      this.isRemovingFromCalendar = true
      GameService.cancelScheduledGame(this.selectedGame.schedule.uuid, this.selectedGame.uuid).then((res) => {
        this.$modal.hide('schedule-view-calendar-game')
        const event = this.$refs.fullCalendar.getApi().getEvents().find(e => e.extendedProps.uuid === this.selectedGame.uuid)
        if (event) event.remove()
        this.fetchGamesToSchedule()
        this.fetchCurrentTimeline()
      }).finally(() => {
        this.isRemovingFromCalendar = false
      })
    },
    async handleViewChange (info) {
      localStorage.setItem('sc_fc_last_view', info.view.type)
      localStorage.setItem('sc_fc_last_date', info.view.currentStart.toISOString())
      this.currentCalendarTitle = info.view.title
      this.currentCalendarType = info.view.type
      this.selectedDate = info.view.currentStart
      this.fetchGamesToSchedule()
    },
    async fetchEvents (fetchInfo, successCallback, failureCallback) {
      this.isCalendarLoading = true
      try {
        const response = await DashboardService.getGamesCalendar(
          fetchInfo.startStr,
          fetchInfo.endStr,
          'all',
          this.selectedVenue?.uuid
        )
        const events = response.data.games.map(game => this.mapGameToEvent(game))
        successCallback(events)
      } catch (error) {
        failureCallback(error)
      } finally {
        this.isCalendarLoading = false
      }
    },
    mapGameToEvent (game) {
      const editable = ['NOT_PLAYED', 'SCHEDULED'].includes(game.status)
      return {
        title: game.shortTitle,
        start: game.time,
        end: game.endTime,
        durationEditable: false,
        editable,
        duration: this.convertMinutesToDuration(game.gameDurationInMinutes),
        extendedProps: game,
        backgroundColor: game.backgroundColor,
        classNames: [this.getColorByStatus(game.status)],
        textColor: game.textColor
      }
    },
    getColorByStatus (status) {
      switch (status) {
        case 'SCHEDULED': return 'border-warning'
        case 'FINISHED':
        case 'ARCHIVED': return 'border-success'
        case 'LIVE': return 'border-danger'
        default: return 'secondary'
      }
    },
    getTeamGamesCount (teamUuid) {
      if (!teamUuid) return { nonPlayedGamesCount: 0, scheduledGamesCount: 0 }
      const team = this.gamesToScheduleTeams.find(t => t.uuid === teamUuid)
      return team ? { nonPlayedGamesCount: team.nonPlayedGamesCount, scheduledGamesCount: team.scheduledGamesCount } : { nonPlayedGamesCount: 0, scheduledGamesCount: 0 }
    },
    getTeamNonPlayedGamesCountClass (teamUuid) {
      if (!teamUuid) return 'badge-success'
      const team = this.gamesToScheduleTeams.find(t => t.uuid === teamUuid)
      if (!team) return 'badge-success'
      return {
        'badge-danger': team.nonPlayedGamesCount > this.teamNonPlayedGamesAverage,
        'badge-warning': team.nonPlayedGamesCount <= this.teamNonPlayedGamesAverage && team.nonPlayedGamesCount > this.teamNonPlayedGamesMin,
        'badge-success': team.nonPlayedGamesCount === this.teamNonPlayedGamesMin
      }
    },
    setOrderByNonPlayedGames (value) {
      this.orderByNonPlayedGames = value
      localStorage.setItem('order-by-non-played-games', value)
    },
    formatDate (date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}`
    },
    convertMinutesToDuration (minutes) {
      const hours = Math.floor(minutes / 60).toString().padStart(2, '0')
      const mins = (minutes % 60).toString().padStart(2, '0')
      return `${hours}:${mins}:00`
    }
  }
}
</script>

<style lang="scss">
.vc-popover-content-wrapper {
  z-index: 9999 !important;
}

.venue-usage-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;

  &--used {
    background-color: #5cb85c;
  }

  &--unused {
    background-color: #d9534f;
  }
}
</style>
